/*                                                                                                                                   */
import { noop, is_function } from '../../../shared/utils.js';
import { effect } from '../../reactivity/effects.js';
import { active_effect, untrack } from '../../runtime.js';
import { loop } from '../../loop.js';
import { should_intro } from '../../render.js';
import { TRANSITION_GLOBAL, TRANSITION_IN, TRANSITION_OUT } from '../../../../constants.js';
import { BLOCK_EFFECT, REACTION_RAN, EFFECT_TRANSPARENT } from '#client/constants';
import { queue_micro_task } from '../task.js';
import { without_reactive_context } from './bindings/shared.js';

/**
 *
 *
 *
 */
function dispatch_event(element, type) {
	without_reactive_context(() => {
		element.dispatchEvent(new CustomEvent(type));
	});
}

/**
 *
 *
 *
 */
function css_property_to_camelcase(style) {
	/*                      */
	if (style === 'float') return 'cssFloat';
	if (style === 'offset') return 'cssOffset';

	/*                               */
	if (style.startsWith('--')) return style;

	const parts = style.split('-');
	if (parts.length === 1) return parts[0];
	return (
		parts[0] +
		parts
			.slice(1)
			.map(/*                    */ (word) => word[0].toUpperCase() + word.slice(1))
			.join('')
	);
}

/**
 *
 *
 */
function css_to_keyframe(css) {
	/*                   */
	const keyframe = {};
	const parts = css.split(';');
	for (const part of parts) {
		const [property, value] = part.split(':');
		if (!property || value === undefined) break;

		const formatted_property = css_property_to_camelcase(property.trim());
		keyframe[formatted_property] = value.trim();
	}
	return keyframe;
}

/*                    */
const linear = (t) => t;

/*                        */
let animation_effect_override = null;

/*                           */
export function set_animation_effect_override(v) {
	animation_effect_override = v;
}

/**
 *
 *
 *
 *
 *
 *
 */
export function animation(element, get_fn, get_params) {
	var effect = animation_effect_override ?? /*                 */ (active_effect);
	var nodes = /*                      */ (effect.nodes);

	/*                  */
	var from;

	/*                  */
	var to;

	/*                                */
	var animation;

	/*                                                                                        */
	var original_styles = null;

	nodes.a ??= {
		element,
		measure() {
			from = this.element.getBoundingClientRect();
		},
		apply() {
			animation?.abort();

			to = this.element.getBoundingClientRect();

			if (
				from.left !== to.left ||
				from.right !== to.right ||
				from.top !== to.top ||
				from.bottom !== to.bottom
			) {
				const options = get_fn()(this.element, { from, to }, get_params?.());

				animation = animate(this.element, options, undefined, 1, () => {
					animation?.abort();
					animation = undefined;
				});
			}
		},
		fix() {
			/*                                                                              */
			/*                                                                                     */
			/*                                                                                         */
			/*                                                                              */
			if (element.getAnimations().length) return;

			/*                                                                                       */
			/*                                                                        */
			var { position, width, height } = getComputedStyle(element);

			if (position !== 'absolute' && position !== 'fixed') {
				var style = /*                                   */ (element).style;

				original_styles = {
					position: style.position,
					width: style.width,
					height: style.height,
					transform: style.transform
				};

				style.position = 'absolute';
				style.width = width;
				style.height = height;
				var to = element.getBoundingClientRect();

				if (from.left !== to.left || from.top !== to.top) {
					var transform = `translate(${from.left - to.left}px, ${from.top - to.top}px)`;
					style.transform = style.transform ? `${style.transform} ${transform}` : transform;
				}
			}
		},
		unfix() {
			if (original_styles) {
				var style = /*                                   */ (element).style;

				style.position = original_styles.position;
				style.width = original_styles.width;
				style.height = original_styles.height;
				style.transform = original_styles.transform;
			}
		}
	};

	/*                                                                                     */
	/*                                                                                     */
	/*                                                                                       */
	/*                         */
	nodes.a.element = element;
}

/**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */
export function transition(flags, element, get_fn, get_params) {
	var is_intro = (flags & TRANSITION_IN) !== 0;
	var is_outro = (flags & TRANSITION_OUT) !== 0;
	var is_both = is_intro && is_outro;
	var is_global = (flags & TRANSITION_GLOBAL) !== 0;

	/*                                */
	var direction = is_both ? 'both' : is_intro ? 'in' : 'out';

	/*                                                                                                 */
	var current_options;

	var inert = element.inert;

	/**
 *
 *
 *
 */
	var overflow = element.style.overflow;

	/*                                */
	var intro;

	/*                                */
	var outro;

	function get_options() {
		return without_reactive_context(() => {
			/*                                                                                   */
			/*                                                                               */
			/*                                                                          */
			return (current_options ??= get_fn()(element, get_params?.() ?? /*            */ ({}), {
				direction
			}));
		});
	}

	/*                            */
	var transition = {
		is_global,
		in() {
			element.inert = inert;

			if (!is_intro) {
				outro?.abort();
				outro?.reset?.();
				return;
			}

			if (!is_outro) {
				/*                                                                         */
				/*                                     */
				intro?.abort();
			}

			intro = animate(element, get_options(), outro, 1, () => {
				dispatch_event(element, 'introend');

				/*                                                */
				intro?.abort();
				intro = current_options = undefined;

				element.style.overflow = overflow;
			});
		},
		out(fn) {
			if (!is_outro) {
				fn?.();
				current_options = undefined;
				return;
			}

			element.inert = true;

			outro = animate(element, get_options(), intro, 0, () => {
				dispatch_event(element, 'outroend');
				fn?.();
			});
		},
		stop: () => {
			intro?.abort();
			outro?.abort();
		}
	};

	var e = /*                                          */ (active_effect);

	(e.nodes.t ??= []).push(transition);

	/*                                                                                    */
	/*                                                                                 */
	/*                                                            */
	if (is_intro && should_intro) {
		var run = is_global;

		if (!run) {
			var block = /*                        */ (e.parent);

			/*                                                           */
			while (block && (block.f & EFFECT_TRANSPARENT) !== 0) {
				while ((block = block.parent)) {
					if ((block.f & BLOCK_EFFECT) !== 0) break;
				}
			}

			run = !block || (block.f & REACTION_RAN) !== 0;
		}

		if (run) {
			effect(() => {
				untrack(() => transition.in());
			});
		}
	}
}

/**
 *
 *
 *
 *
 *
 *
 *
 */
function animate(element, options, counterpart, t2, on_finish) {
	var is_intro = t2 === 1;

	if (is_function(options)) {
		/*                                                                           */
		/*                                                                         */
		/*                               */
		/*                    */
		var a;
		var aborted = false;

		queue_micro_task(() => {
			if (aborted) return;
			var o = options({ direction: is_intro ? 'in' : 'out' });
			a = animate(element, o, counterpart, t2, on_finish);
		});

		/*                                                                   */
		/*                                                              */
		return {
			abort: () => {
				aborted = true;
				a?.abort();
			},
			deactivate: () => a.deactivate(),
			reset: () => a.reset(),
			t: () => a.t()
		};
	}

	counterpart?.deactivate();

	if (!options?.duration && !options?.delay) {
		dispatch_event(element, is_intro ? 'introstart' : 'outrostart');
		on_finish();

		return {
			abort: noop,
			deactivate: noop,
			reset: noop,
			t: () => t2
		};
	}

	const { delay = 0, css, tick, easing = linear } = options;

	var keyframes = [];

	if (is_intro && counterpart === undefined) {
		if (tick) {
			tick(0, 1); /*                                                            */
		}

		if (css) {
			var styles = css_to_keyframe(css(0, 1));
			keyframes.push(styles, styles);
		}
	}

	var get_t = () => 1 - t2;

	/*                                                                                   */
	/*                                                                                     */
	/*                                                        */
	//
	/*                                                                         */
	/*                                                  */
	var animation = element.animate(keyframes, { duration: delay, fill: 'forwards' });

	animation.onfinish = () => {
		/*                                                                            */
		animation.cancel();

		dispatch_event(element, is_intro ? 'introstart' : 'outrostart');

		/*                                                                 */
		/*                                   */
		var t1 = counterpart?.t() ?? 1 - t2;
		counterpart?.abort();

		var delta = t2 - t1;
		var duration = /*                 */ (options.duration) * Math.abs(delta);
		var keyframes = [];

		if (duration > 0) {
			/**
 *
 *
 *
 */
			var needs_overflow_hidden = false;

			if (css) {
				var n = Math.ceil(duration / (1000 / 60)); /*                                                        */

				for (var i = 0; i <= n; i += 1) {
					var t = t1 + delta * easing(i / n);
					var styles = css_to_keyframe(css(t, 1 - t));
					keyframes.push(styles);

					needs_overflow_hidden ||= styles.overflow === 'hidden';
				}
			}

			if (needs_overflow_hidden) {
				/*                      */ (element).style.overflow = 'hidden';
			}

			get_t = () => {
				var time = /*                 */ (
					/*                               */ (animation).currentTime
				);

				return t1 + delta * easing(time / duration);
			};

			if (tick) {
				loop(() => {
					if (animation.playState !== 'running') return false;

					var t = get_t();
					tick(t, 1 - t);

					return true;
				});
			}
		}

		animation = element.animate(keyframes, { duration, fill: 'forwards' });

		animation.onfinish = () => {
			get_t = () => t2;
			tick?.(t2, 1 - t2);
			on_finish();
		};
	};

	return {
		abort: () => {
			if (animation) {
				animation.cancel();
				/*                                     */
				animation.effect = null;
				/*                                                    */
				/*                                  */
				/*                                                  */
				animation.onfinish = noop;
			}
		},
		deactivate: () => {
			on_finish = noop;
		},
		reset: () => {
			if (t2 === 0) {
				tick?.(1, 0);
			}
		},
		t: () => get_t()
	};
}
